<template>
    <!--
        T30 (walk 2026-09-18, Log #360, #367): THE ONE MODAL SHELL. It is `b-modal` wearing the merged
        sheet's F5 block, and it is NOT a new dialogue mechanism: the id, `$bvModal.show(id)` / `.hide(id)`,
        `@show` / `@shown` / `@hide` / `@hidden`, `no-close-on-backdrop`, `lazy`, `static` and every other
        `b-modal` attribute or listener a caller writes on `<AFModal>` reach the `b-modal` inside untouched
        (`$attrs` and `$listeners`), and a `ref` on it still answers `show()`, `hide()` and `toggle()`.

        The reference is `walk/2026-09-18/mock/design-walk/r9-modal-shell-and-action-modals.html`:

            div.ftd-modal[.ftd-modal(sm|lg)]
                div.ftd-modal__head > span.ftd-modal__title (> span.ftd-modal__sub) , button.ftd-modal__x
                (div.ftd-modal__banner)
                div.ftd-modal__body
                div.ftd-modal__foot.ftd-modal__foot(right) > button.ftd-pill.ftd-pill(danger) Cancel , the one submit

        HOW THE HOUSE CLASSES REACH BOOTSTRAPVUE'S OWN ELEMENTS is T33a's way, the one `presentConfirmBox`
        uses, so the shell and the confirm are drawn by the same rules: `modal-class="ftd"` is the ancestor
        the sheet's `.ftd .ftd-something` rules need (a modal is portalled to `<body>`, outside every `.ftd`
        on the page), and `content-class` / `header-class` / `title-class` / `body-class` / `footer-class`
        put the F5 classes on the five elements `b-modal` renders. The title is a `<span>`, as the mock's is
        (an `h5` takes the theme's grey between 768 and 1199 wide: `notes/T33a.md` R3). The close cross takes
        its house class through `header-close-variant`, the one way in BootstrapVue 2.23.1 has
        (`none` is deliberately not a variant the theme knows).

        THE SIZE CLASS IS ON THE `.ftd-modal` ELEMENT, where the mock writes it, and the three widths are the
        sheet's own (`.ftd-modal` 760px, `.ftd-modal(sm)` 440px, `.ftd-modal(lg)` 1060px). BootstrapVue's own
        `size` is not passed. `af-modal` is this component's one hook class: `resources/sass/custom/
        _ftd-modal-shell.scss` keys on it to stop Bootstrap's `.modal-dialog` capping the width first.

        THE BODY SCROLLS AND THE HEAD AND FOOT DO NOT: `scrollable` is Bootstrap's own mechanism for it
        (`.modal-dialog-scrollable`), and the sheet's `.ftd-modal__body { overflow-y: auto }` agrees with it.

        AN OPEN PICKER'S LIST LIES OVER THE DIALOGUE'S EDGE (follow-up 1, the hand-off's Amendment 13 item 2).
        vue-multiselect's option list is `position: absolute` under its control, and a body that scrolls
        clips whatever hangs out of it. The sheet's second pass (2026-09-21) took the clip off the shell
        (`.ftd-modal { overflow: visible }`) and gave the body an opt-out, `.ftd-modal__body--overflow`, for
        "a dialogue whose content fits the room it has AND which opens a picker". The shell says so for every
        adopter, with nothing to do in the caller: WHILE the focus is inside a picker of the body (which is
        when vue-multiselect is open: it opens on focus and closes on blur) AND the body's content fits its
        pane, the body carries the class. Bootstrap's own `.modal-dialog-scrollable .modal-content
        { overflow: hidden }` and `.modal-dialog-scrollable .modal-body { overflow-y: auto }` are (0,2,0) and
        beat the sheet's two (0,1,0) rules, so for exactly as long `scrollable` is off as well: a body that
        fits has nothing to scroll, and the dialogue measures the same with and without it. A body that is
        too tall never takes the class: it keeps scrolling, which is the sheet's safe default, and the list
        is inside its scroll. See `pickerFocus()` below. (Fix round 1's spacer, `div.af-modal__room`, and its
        `MutationObserver` answered the clip the sheet no longer applies, and are gone.)
    -->
    <b-modal ref="modal"
             :id="id"
             v-bind="$attrs"
             v-on="modal_listeners"
             modal-class="ftd af-modal"
             :content-class="content_class"
             header-class="ftd-modal__head"
             title-class="ftd-modal__title"
             title-tag="span"
             :body-class="body_class"
             footer-class="ftd-modal__foot ftd-modal__foot--right"
             header-close-variant="none ftd-modal__x"
             :hide-footer="hideFooter"
             :scrollable="!body_overflow"
             @show="themeOn"
             @shown="onShown"
             @hidden="onHidden">

        <template #modal-title>
            <slot name="title">{{ title }}</slot><span v-if="sub" class="ftd-modal__sub">{{ sub }}</span>
        </template>

        <template #modal-header-close><i class="bx bx-x" aria-hidden="true"></i><span class="ftd-sr">Close</span></template>

        <div v-if="banner || $slots.banner" class="ftd-modal__banner">
            <slot name="banner"><i class="bx bx-lock-alt"></i><span>{{ banner }}</span></slot>
        </div>

        <slot/>

        <template #modal-footer>
            <slot name="footer">
                <div v-if="$slots['foot-left']" class="ftd-modal__left"><slot name="foot-left"/></div>
                <button type="button"
                        class="ftd-pill"
                        :class="{'ftd-pill--danger': cancelTone === 'danger'}"
                        @click="cancelClicked">{{ cancelText }}</button>
                <slot name="submit"/>
            </slot>
        </template>
    </b-modal>
</template>

<script>
/*
 * PROPS
 *   id          the `b-modal` id, exactly the one the caller had. Ids are NOT renumbered: two instances
 *               that share an id answer `$bvModal.show(id)` together, as they did.
 *   title       today's title expression.
 *   sub         the second line of a title that prints two things (`.ftd-modal__sub`); omitted, no line.
 *   size        'sm' (440px, a confirm or one input), '' / 'md' (760px, the default), 'lg' (1060px, an
 *               action with sections). Three sizes and no more.
 *   banner      the read-only banner's sentence (`.ftd-modal__banner`); or use the `banner` slot.
 *   hideFooter  no footer at all (a body that still carries its own, e.g. RegisterDispositionFields).
 *   cancelText  the Cancel pill's word, default "Cancel" ("Close" on the QR dialogue).
 *   cancelTone  'danger' (default: the red-outlined pill of Log #367) or 'plain' (a confirm's NO / Cancel).
 *   Any other attribute is `b-modal`'s and is passed to it (`no-close-on-backdrop`, `lazy`, `static` ...).
 *
 * SLOTS
 *   default     the body, exactly the body the dialogue had.
 *   submit      the ONE submit, drawn after Cancel so it is the rightmost control.
 *   foot-left   what belongs at the left of the footer (`.ftd-modal__left`).
 *   footer      replaces the whole footer content (the pair included), for a footer that is not the pair.
 *   title       replaces the title's text (the `sub` line is still drawn after it).
 *   banner      replaces the banner's content.
 *
 * EVENTS
 *   cancel      the Cancel pill was pressed. A caller that listens does its own closing (today's
 *               `cancelClicked`); with no listener the shell hides itself with the trigger 'cancel'.
 *   every `b-modal` event (`show`, `shown`, `hide`, `hidden`, `ok`, `close`, `change`) is `b-modal`'s own
 *   and reaches the caller's listener with `b-modal`'s own payload.
 *
 * METHODS (so a `ref` keeps working): show(), hide(trigger), toggle().
 */
export default {
    name: 'AFModal',
    inheritAttrs: false,
    props: {
        id: { type: String, default: undefined },
        title: { type: String, default: '' },
        sub: { type: String, default: '' },
        size: { type: String, default: '' },
        banner: { type: String, default: '' },
        hideFooter: { type: Boolean, default: false },
        cancelText: { type: String, default: 'Cancel' },
        cancelTone: { type: String, default: 'danger' }
    },
    data() {
        return {
            // the body lets an open picker's list hang out of it (`.ftd-modal__body--overflow`): see `pickerFocus()`
            body_overflow: false
        };
    },
    beforeDestroy() {
        this.pickerListenOff();
    },
    computed: {
        content_class() {
            const classes = ['ftd-modal'];
            if (this.size === 'sm' || this.size === 'lg') {
                classes.push('ftd-modal--' + this.size);
            }
            return classes;
        },
        body_class() {
            return this.body_overflow ? ['ftd-modal__body', 'ftd-modal__body--overflow'] : ['ftd-modal__body'];
        },
        // `cancel` is this shell's own event (the pill); `b-modal` emits a `cancel` of its own whenever it
        // hides with that trigger, so the caller's listener is kept off it or it would run twice.
        modal_listeners() {
            const listeners = Object.assign({}, this.$listeners);
            delete listeners.cancel;
            return listeners;
        }
    },
    methods: {
        show() {
            this.$refs.modal.show();
        },
        hide(trigger) {
            this.$refs.modal.hide(trigger);
        },
        toggle(trigger_element) {
            this.$refs.modal.toggle(trigger_element);
        },
        cancelClicked(event) {
            if (this.$listeners.cancel) {
                this.$emit('cancel', event);
                return;
            }
            this.hide('cancel');
        },
        onShown() {
            this.themeOn();
            this.pickerListenOn();
        },
        onHidden() {
            this.pickerListenOff();
        },
        /*
         * AN OPEN PICKER (follow-up 1; the template's comment says why). vue-multiselect opens when the
         * focus comes into it and closes when the focus leaves it, so the two focus events that bubble are
         * all that is listened for, on the body, and only while the dialogue is shown. No observer and no
         * element of the shell's own.
         */
        bodyElement() {
            const modal = this.$refs.modal;
            return modal && modal.$refs && modal.$refs.body ? modal.$refs.body : null;
        },
        pickerListenOn() {
            this.pickerListenOff();
            const body = this.bodyElement();
            if (!body || typeof body.addEventListener !== 'function') {
                return;
            }
            this.picker_body = body;
            this.picker_focus_in = (event) => this.pickerFocus(event.target);
            // the focus that moves from the picker's control to its own search box has not left the picker
            this.picker_focus_out = (event) => this.pickerFocus(event.relatedTarget);
            body.addEventListener('focusin', this.picker_focus_in);
            body.addEventListener('focusout', this.picker_focus_out);
            // a dialogue that opens with the focus already in a picker
            this.pickerFocus(typeof document !== 'undefined' ? document.activeElement : null);
        },
        pickerListenOff() {
            if (this.picker_body) {
                this.picker_body.removeEventListener('focusin', this.picker_focus_in);
                this.picker_body.removeEventListener('focusout', this.picker_focus_out);
            }
            this.picker_body = null;
            this.picker_focus_in = null;
            this.picker_focus_out = null;
            clearTimeout(this.picker_leaving);
            this.picker_leaving = null;
            this.body_overflow = false;
        },
        // `focused` is the element that has (or is about to have) the focus, or null.
        pickerFocus(focused) {
            const body = this.picker_body;
            if (!body) {
                return;
            }
            if (this.pickerIsOpenAt(body, focused)) {
                clearTimeout(this.picker_leaving);
                this.picker_leaving = null;
                this.body_overflow = true;
                return;
            }
            if (!this.body_overflow || this.picker_leaving) {
                return;
            }
            // vue-multiselect fades its list out over .15s: the body does not start clipping under a list
            // that is still being drawn. Where the focus stands once it has gone is what decides.
            this.picker_leaving = setTimeout(() => {
                this.picker_leaving = null;
                const now = typeof document !== 'undefined' ? document.activeElement : null;
                this.body_overflow = !!this.picker_body && this.pickerIsOpenAt(this.picker_body, now);
            }, 200);
        },
        pickerIsOpenAt(body, focused) {
            const picker = focused && typeof focused.closest === 'function' ? focused.closest('.multiselect') : null;
            return !!picker && body.contains(picker) && this.bodyFits(body);
        },
        /*
         * Whether the body's content fits its pane, so that giving up the scroll costs nothing. It is read
         * from the body's own children and not from `scrollHeight`, because an open list counts in
         * `scrollHeight` and is the one thing that must not: a list that hangs out is the point.
         */
        bodyFits(body) {
            if (body.scrollTop > 0) {
                return false;
            }
            const style = typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(body) : null;
            const padding = style ? (parseFloat(style.paddingBottom) || 0) : 0;
            const border = style ? (parseFloat(style.borderBottomWidth) || 0) : 0;
            const floor = body.getBoundingClientRect().bottom - border - padding;
            let lowest = -Infinity;
            for (let child = body.firstElementChild; child; child = child.nextElementSibling) {
                lowest = Math.max(lowest, child.getBoundingClientRect().bottom);
            }
            return lowest <= floor + 1;
        },
        /*
         * The entity's four colours, as `FilmTvDashboard.vue`, `AssetInfoSection.vue` and T33a's
         * `ftdThemeOn` read them. `.ftd` re-declares the four variables, so an inherited value never reaches
         * it, and `b-modal` takes no style for its `.modal` element: they are set on the element itself,
         * found through `b-modal`'s own ref and not by id, because ids collide on this site.
         */
        themeOn() {
            const plate = (typeof window !== 'undefined' && window.COLOUR_PLATE) ? window.COLOUR_PLATE : null;
            if (!plate) {
                return;
            }
            const variables = {
                '--ftd-primary': plate.primary_colour,
                '--ftd-accent': plate.accent_colour,
                '--ftd-primary-soft': plate.soft_primary_colour,
                '--ftd-accent-soft': plate.soft_accent_colour
            };
            this.$nextTick(() => {
                const modal = this.$refs.modal;
                const el = modal && modal.$refs ? modal.$refs.modal : null;
                if (!el || !el.style) {
                    return;
                }
                Object.keys(variables).forEach(function (name) {
                    if (variables[name]) {
                        el.style.setProperty(name, variables[name]);
                    }
                });
            });
        }
    }
}
</script>
